import React, { useEffect, useRef, useState } from 'react';

import Text from '~components/atoms/Text';
import TabPageContainer from '~components/atoms/TabPageContainer';
import TabPageHeaderWrapper from '~components/atoms/TabPageHeaderWrapper';

import * as Styles from './customertransfer.module.scss';
import ActionButton from '~components/atoms/ActionButton';
import DownloadCSV from '~components/molecules/DownloadCSV';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import UtilFunctions from '~utils/functions';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { MetalTypeEnum, ModuleEnum, PermissionsEnum, TransactionStatusEnum } from '~utils/enums';
import combinedActions from '~redux/combinedActions';
import CustomerTransferSearchAndFilterBar from './CustomerTransferSearchAndFilterBar';
import CustomerTransferTable from './CustomerTransferTable';
import CreateCustomerTransferModal from './CreateCustomerTransferModal';
import CustomerTransferPaginator from './CustomerTransferPaginator';
import DetailsFormCustomerTransferModal from './DetailsFormCustomerTransferModal';
import CustomerTransferDetailsModal from './CustomerTransferDetailsModal';

const CustomerTransfer = () => {
    const dispatch = useAppDispatch();
    const {
        Misc: {
            dateTimeFormat,
        },
    } = UtilFunctions;

    const customerTransferListingForExport = useAppSelector(state => state.customerTransfer.customerTransferExportListingForExport);
    const getExportCustomerTransferListingForExportIsLoading = useAppSelector(state => state.customerTransfer.loadings.getCustomerTransferListingExport);
    const customerTransferListingIndex = useAppSelector(state => state.customerTransfer.customerTransferListing.index);

    const createAccessModules = useAppSelector(state => state.auth.userDetails.access)?.find(access => access.permission === PermissionsEnum.Create)?.modules || [];

    const isReadyToExportRef = useRef(false);
    const prevLoadingRef = useRef(false);

    const exportCustomerTransferListing = () => {
        isReadyToExportRef.current = true; 
        dispatch(combinedActions.customerTransferGetExportCustomerTransferAttempt());
    };
    const clearCustomerTransferListingForExport = () => dispatch(combinedActions.customerTransferClearCustomerTransferListingForExport());

    const [isExportingCustomerTransferListing, setIsExportingCustomerTransferListing] = useState(false);

    useEffect(() => {
        dispatch(combinedActions.customerTransferGetCustomerTransferListingAttempt());
    }, [customerTransferListingIndex]);

    const handleCreateNewCustomerTransfer = () => {
        dispatch(combinedActions.customerTransferSetCreateCustomerTransferModalIsOpen(true));
        dispatch(combinedActions.customerTransferCreateCustomerTransferDraftAttempt());
    };

    const listHeader = [
        'TRANSACTION ID',
        'SENDER NAME',
        'SENDER CIF',
        'PRODUCT NAME',
        'METAL TYPE',
        'SERIAL NUMBER',
        'TOTAL WEIGHT (G)',
        'STATUS',
        'RECEIVER NAME',
        'RECEIVER CIF',
        'BRANCH CODE',
        'BRANCH NAME',
        'REGION NAME',
        'BRANCH TELLER NAME',
        'SALES PERSONNEL NAME',
        'REFERRAL NAME',
        'TRANSACTION DATE',
    ];

    const listData = customerTransferListingForExport.map(transaction => {
        const {
            customerTransferId,
            senderName,
            senderCIFNo,
            productName,
            metalType,
            serialNumbers,
            weight,
            status,
            receiverName,
            receiverCIFNo,
            branchCode,
            branchName,
            regionName,
            branchTellerName,
            salesPersonnelName,
            referralName,
            createdAt,
        } = transaction;

        const massagedSerialNumbers = (serialNumbers || [])
            .filter(item => item)
            .join('|');

        return {
            customerTransferId: customerTransferId || '-',
            senderName: senderName || '-',
            senderCIFNo: senderCIFNo || '-',
            productName: productName || '-',
            metalType: MetalTypeEnum[metalType] || '-',
            serialNumbers: massagedSerialNumbers.length > 0 ? massagedSerialNumbers : '-',
            weight: weight !== null && weight !== undefined
                ? Decimal(weight).toFixed(3)
                : '-',
            status: TransactionStatusEnum[status] || '-',
            receiverName: receiverName || '-',
            receiverCIFNo: receiverCIFNo || '-',
            branchCode: branchCode || '-',
            branchName: branchName || '-',
            regionName: regionName || '-',
            branchTellerName: branchTellerName || '-',
            salesPersonnelName: salesPersonnelName || '-',
            referralName: referralName || '-',
            createdAt: dateTimeFormat(createdAt) || '-',
        };
    });

    const handleExportCustomerTransferListingCleanup = () => {
        clearCustomerTransferListingForExport();
        setIsExportingCustomerTransferListing(false);
        isReadyToExportRef.current = false;
    };

    useEffect(() => {
        if (prevLoadingRef.current && !getExportCustomerTransferListingForExportIsLoading && isReadyToExportRef.current) {
            if (listData.length === 0) {
                const csv = listHeader.join(',') + '\r\n';
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${dayjs().format('DD-MM-YYYY')} Customer Transfer Listing.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                handleExportCustomerTransferListingCleanup();
            }
        }

        prevLoadingRef.current = getExportCustomerTransferListingForExportIsLoading;
    }, [getExportCustomerTransferListingForExportIsLoading]);

    return (
        <TabPageContainer>
            <TabPageHeaderWrapper>
                <Text
                    label='Customer Transfer'
                    className={Styles.title}
                    isBold
                />

                <div className={Styles['action-container']}>
                    {createAccessModules.includes(ModuleEnum.CustomerTransfer) && (
                        <ActionButton
                            testId='create-customer-transfer-modal-open-button'
                            label='Create New Customer Transfer'
                            onClick={handleCreateNewCustomerTransfer}
                        />
                    )}
                    <DownloadCSV
                        exportTestId='customerTransfer-transaction-export-button'
                        buttonLabel='Export List to CSV'
                        header={listHeader}
                        data={listData}
                        fileName={dayjs().format('DD-MM-YYYY').concat(' Customer Transfer Listing')}
                        onClick={exportCustomerTransferListing}
                        cleanup={handleExportCustomerTransferListingCleanup}
                        isLoading={getExportCustomerTransferListingForExportIsLoading || isExportingCustomerTransferListing}
                    />
                </div>
            </TabPageHeaderWrapper>

            <CustomerTransferSearchAndFilterBar />
            <CustomerTransferTable />
            <CreateCustomerTransferModal />
            <CustomerTransferPaginator />
            <CustomerTransferDetailsModal />
        </TabPageContainer>
    );
};

export default CustomerTransfer;
